import { useCallback } from 'react';
import { GetALLUsersForPreviewSession, UserData } from '@/apis/onboarding';
import {
  useLiteDropdown,
  UseLiteDropdownOptions,
} from '@/common/helper/useLiteDropdown';
import {
  PaginatedRequest,
  PaginatedResponse,
} from '@/common/helper/usePaginatedDropdown';
import useToken from '@/common/helper/useToken';

export interface UserLiteTeam {
  id?: string;
  name?: string;
  team_name?: string;
  role_name?: string;
  color?: string;
  [key: string]: unknown;
}

export interface UserLiteItem {
  id: string;
  name: string;
  email?: string;
  teams?: UserLiteTeam[];
  is_default_admin?: boolean;
  [key: string]: unknown;
}

export interface UseUserLiteDropdownParams extends Partial<
  Pick<UseLiteDropdownOptions<UserLiteItem>, 'initialLimit' | 'initialSearch'>
> {
  unauthorize?: (error: unknown) => void;
  token?: string;
}

/**
 * Domain hook for the users lite dropdown.
 *
 * Calls Settings Users API (GET /auth/users/) with pagination and search.
 * Delegates scroll handling and pagination state to useLiteDropdown.
 */
export const useUserLiteDropdown = ({
  initialLimit = 10,
  initialSearch = '',
  unauthorize,
  token: explicitToken,
}: UseUserLiteDropdownParams = {}) => {
  const { token: contextToken } = useToken();
  const token = explicitToken || contextToken;

  const fetchFn = useCallback(
    async ({
      page,
      search,
    }: PaginatedRequest): Promise<PaginatedResponse<UserData>> => {
      try {
        const response = await GetALLUsersForPreviewSession(
          token,
          page,
          search || undefined
        );
        const resData = response?.data?.data;
        const rawItems = Array.isArray(resData?.data) ? resData.data : [];
        const total = resData?.total_count;
        const pageCount = resData?.page_count ?? 1;
        return {
          data: rawItems,
          total,
          pageCount,
          hasNext: page < pageCount,
        };
      } catch (error) {
        unauthorize?.(error);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [token, unauthorize]
  );

  const lite = useLiteDropdown<UserData>({
    fetchFn,
    initialLimit,
    initialSearch,
  });

  return {
    /** User items for the current loaded pages */
    userList: lite.data,
    users: lite.data,
    hasMore: lite.hasMore,
    loading: lite.loading,
    search: lite.search,
    setSearch: lite.setSearch,
    loadMore: lite.loadMore,
    handlePopupScroll: lite.handlePopupScroll,
    fetchAllMatching: lite.fetchAllMatching,
  };
};

export default useUserLiteDropdown;
